import { useState } from "react";
import { useTranslation } from "react-i18next";
import { XCircle } from "lucide-react";
import { logger } from "@/lib/logger";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { notifyDefectStatusChanged } from "@/lib/notifications";
import { TruncatedText } from "@/components/ui/truncated-text";

interface CancelDefectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defect: { id: string; title: string; assigned_to?: string | null } | null;
  onCancelled?: () => void;
}

// TES-2229 (LH §L5.10, Mig 399): Storno läuft ausschließlich über den Server-Kanal
// rpc_cancel_defect (Rechte: Ersteller im Korrektur-Fenster ∨ QM ∨ Verifizierer/Deputy;
// Begründungspflicht DB-erzwungen). Der frühere direkte Tabellen-UPDATE ist serverseitig
// abgeschafft (guard_defect_status_transition, P0409).
type CancelDefectRpc = {
  rpc: (
    fn: "rpc_cancel_defect",
    args: { _defect_id: string; _reason: string },
  ) => Promise<{ error: { code?: string; message: string } | null }>;
};

/** SQLSTATE→i18n-Mapping (Vertrag Mig 399: P0422/P0409/P0403 — Muster VerifyDefectDialog). */
function cancelErrorKey(code?: string): string {
  switch (code) {
    case "P0403":
      return "defects.cancelDialog.notPermitted";
    case "P0422":
      return "defects.cancelDialog.reasonRequired";
    case "P0409":
      return "defects.cancelDialog.invalidState";
    default:
      return "defects.cancelDialog.errorDescription";
  }
}

export function CancelDefectDialog({
  open,
  onOpenChange,
  defect,
  onCancelled,
}: CancelDefectDialogProps) {
  const { t } = useTranslation();

  const [reason, setReason] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const trimmedReason = reason.trim();

  const handleCancel = async () => {
    if (!defect || !trimmedReason) return;

    setIsSubmitting(true);
    try {
      const { error } = await (supabase as unknown as CancelDefectRpc).rpc("rpc_cancel_defect", {
        _defect_id: defect.id,
        _reason: trimmedReason,
      });

      if (error) {
        logger.error("Error cancelling defect:", error);
        toast.error(t("common.error"), {
          description: t(cancelErrorKey(error.code)),
        });
        return;
      }

      // Notify assigned user about cancellation – use prop instead of re-fetching
      if (defect.assigned_to) {
        notifyDefectStatusChanged(defect.assigned_to, defect.title, defect.id, "cancelled");
      }

      toast.success(t("defects.cancelDialog.successTitle"), {
        description: t("defects.cancelDialog.successDescription"),
      });

      setReason("");
      onOpenChange(false);
      onCancelled?.();
    } catch (error) {
      logger.error("Error cancelling defect:", error);
      toast.error(t("common.error"), {
        description: t("defects.cancelDialog.errorDescription"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setReason("");
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md w-[calc(100%-2rem)]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-muted-foreground" />
            {t("defects.cancelDialog.title")}
          </DialogTitle>
          <DialogDescription>{t("defects.cancelDialog.description")}</DialogDescription>
        </DialogHeader>

        {defect && (
          <div className="space-y-4">
            <div className="p-3 bg-muted rounded-md min-w-0">
              <TruncatedText text={defect.title} className="font-medium" maxLines={2} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="reason">{t("defects.cancelDialog.reason")}</Label>
              <Textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t("defects.cancelDialog.reasonPlaceholder")}
                rows={3}
                data-testid="defect-cancel-reason"
              />
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isSubmitting}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="destructive"
            onClick={handleCancel}
            disabled={isSubmitting || !trimmedReason}
            data-testid="defect-cancel-confirm"
          >
            {isSubmitting ? t("common.saving") : t("defects.cancelDialog.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
